import React, { CSSProperties, ReactNode } from "react";
import { GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import styled from "styled-components";

import { Container } from "@util/standard";
import { MOBILE_BREAKPOINT } from "@util/constants";
import { PlaceholderImage } from "@global";

const StyledImage = styled(GatsbyImage) <{
  width?: string;
  height?: string;
  margin?: string;
  mobWidth?: string;
  isMobile?: boolean;
  hasMobile?: boolean;
}>`
  width: ${props => props.width ?? "100%"};
  height: ${props => props.height ?? "100%"};
  ${({ margin }) => margin && `margin: ${margin};`}
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px) {
    width: ${props => props.mobWidth};
  }

  ${({ hasMobile }) =>
    hasMobile &&
    `
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px){
    display: none;
  }
  `}

  ${({ isMobile }) =>
    isMobile &&
    `
  display: none;
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px){
    display: block;
  }
  `};
`;

const BackgroundGatsbyImage = styled(GatsbyImage) <{
  isMobile?: boolean;
  hasMobile?: boolean;
}>`
  position: absolute !important;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;

  ${({ hasMobile }) =>
    hasMobile &&
    `
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px){
    display: none;
  }
  `}

  ${({ isMobile }) =>
    isMobile &&
    `
  display: none;
  @media only screen and (max-width: ${MOBILE_BREAKPOINT}px){
    display: block;
  }
  `};
`;

const BackgroundImageWrapper = styled.div<{
  minHeight?: string;
  height?: string;
}>`
  display: flex;
  width: 100%;
  margin: 0;
  position: relative;
  overflow: hidden;
  min-width: 100%;
  background-color: #E5E5E5;
  ${({ minHeight }) => minHeight && `min-height: ${minHeight};`}
  ${({ height }) => height && `min-height: ${height};`}
`;

const StyledImg = styled.img<{
  objectFit?: string;
}>`
  cursor: pointer;
  ${({ objectFit }) => `
    object-fit: ${objectFit};
  `}
`;
interface Props {
  imageData?: IGatsbyImageData | Record<string, unknown> | undefined;
  svg?: Queries.Maybe<string> | undefined;
  minHeight?: string;
  isBackground?: boolean;
  width?: string;
  height?: string;
  altText?: string;
  children?: ReactNode;
  onClick?: () => void;
  objectFit?: CSSProperties["objectFit"] | string;
  margin?: string;
  mobWidth?: string;
  staticImage?: Queries.Maybe<string>;
  mobileImage?: IGatsbyImageData | Record<string, unknown> | undefined;
}

const Image = (props: Props) => {
  const { minHeight } = props;
  if (props.isBackground && props.imageData) {
    return (
      <BackgroundImageWrapper
        minHeight={props.minHeight}
        height={props.height}
      >
        <BackgroundGatsbyImage
          image={props.imageData as IGatsbyImageData}
          alt={`background image ${props.altText || ""}`}
          hasMobile={Boolean(props.mobileImage)}
          objectFit={(props.objectFit as CSSProperties["objectFit"]) ?? "cover"}
        />
        {props.mobileImage && (
          <BackgroundGatsbyImage
            image={props.mobileImage as IGatsbyImageData}
            alt={`background image ${props.altText || ""}`}
            objectFit={(props.objectFit as CSSProperties["objectFit"]) ?? "cover"}
            isMobile
          />
        )}
        <Container position="relative" width="100%">
          {props.children}
        </Container>
      </BackgroundImageWrapper>
    );
  } else if (props.isBackground) {
    return (
      <Container position="relative" width="100%">
        {props.children}
      </Container>
    );
  }

  if (props.staticImage) {
    return (
      <StyledImg
        src={props.staticImage}
        width={props.width}
        height={props.height}
        style={{ minHeight }}
        itemProp="image"
        alt={props.altText ?? "Everyday by Barker's"}
        onClick={props.onClick}
        objectFit={props.objectFit ?? "contain"}
      />
    );
  }

  if (props.imageData) {
    return (
      <>
        <StyledImage
          image={props.imageData as IGatsbyImageData}
          style={{ minHeight }}
          width={props.width}
          height={props.height}
          itemProp="image"
          alt={props.altText ?? "Barker's of Geraldine"}
          onClick={props.onClick}
          objectFit={(props.objectFit as CSSProperties["objectFit"]) ?? "contain"}
          margin={props.margin}
          mobWidth={props.mobWidth}
          hasMobile={Boolean(props.mobileImage)}
        />
        {props.mobileImage && (
          <StyledImage
            image={props.mobileImage as IGatsbyImageData}
            alt={`background image ${props.altText || ""}`}
            objectFit={(props.objectFit as CSSProperties["objectFit"]) ?? "cover"}
            width={props.width}
            height={props.height}
            isMobile
          />
        )}
      </>
    );
  }

  return (
    <PlaceholderImage
      width={props.width ?? "100%"}
      height={props.height ?? "100%"}
      altText={props.altText ?? "Barker's of Geraldine"}
    />
  );
};

export default Image;
